/**
 * Sentry browser init.
 *
 * No-op when VITE_SENTRY_DSN is unset (= local dev without a DSN, or
 * any preview build). In production we bake the DSN at `vite build`
 * time via the env var.
 *
 * Vite replaces `import.meta.env.VITE_SENTRY_DSN` with the literal
 * value at build time, so an unset env produces a `false`-y check that
 * dead-code-eliminates the @sentry/browser import in the production
 * bundle (saves ~60KB gzipped if you don't want SaaS error reporting).
 *
 * See SCALING_AUDIT.md S-15.
 */

export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info("[sentry] VITE_SENTRY_DSN unset — error reporting disabled");
    }
    return;
  }
  // Dynamic import so the lib (~60KB gz) only loads when the DSN is
  // configured. In a build with no DSN, the entire import is dropped.
  const Sentry = await import("@sentry/browser");
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    // No perf tracing — pure error capture for now.
    tracesSampleRate: 0,
    // Drop noise from extensions / cross-origin script errors.
    ignoreErrors: ["ResizeObserver loop limit exceeded", "Non-Error promise rejection captured"],
  });
}
